import { useState, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import { Icon } from "@zapier/zinnia-icons";
import { Button, Grid, Tag, Text } from "@zapier/design-system";

import { GradientBorder } from "../GradientBorder";
import { useStreamingCompletion } from "../../hooks/useStreamingCompletion";
import { fixAndParseJSON } from "../../utils/fixAndParseJSON";
import type { PromptPlan } from "../../types/PromptPlan";
import { useUserContext } from "../../hooks/useUserContext";
import {
  enhancedPromptAtom,
  enhancedPromptSetter,
} from "../../atoms/enhancedPromptAtom";

import { AiPromptBuilder } from "./AiPromptBuilder";
import styles from "./AiPromptBox.module.css";

type AiPromptBoxProps = {
  handleSubmit: (prompt: string) => void;
  emitUserInteractionEvent: (event: {
    eventAction: string;
    interactionGoal: string;
    rawText?: string;
  }) => void;
  showAgentsNodes: boolean;
};

export function AiPromptBox({
  handleSubmit,
  emitUserInteractionEvent,
  showAgentsNodes,
}: AiPromptBoxProps) {
  const userContext = useUserContext();

  const [prompt, setPrompt] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [eventSent, setEventSent] = useState(false);
  const appendEnhancedPrompt = useSetAtom(enhancedPromptSetter);
  const [enhancedPrompt, setEnhancedPrompt] = useAtom(enhancedPromptAtom);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const textEntered = event.target.value;
    setPrompt(textEntered);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set new height
    }

    // Send the event only on the first input change
    if (!eventSent) {
      await emitUserInteractionEvent({
        eventAction: "edit",
        interactionGoal: "Entered an AI prompt from list page",
      });
      setEventSent(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        // Allow Shift+Enter for new line
        return;
      }
      // Prevent default behavior (new line)
      event.preventDefault();

      if (!isStreaming && prompt.trim()) {
        handleGenerate();
      }
    }
  };

  const { triggerRequest } = useStreamingCompletion({
    api: "/api/zsl/canvas/prompter",
    onMessage: (msg) => {
      appendEnhancedPrompt(msg.data);
    },
    onFinish: () => {
      setIsStreaming(false);
    },
  });

  let thePlan: PromptPlan | null = null;

  if (enhancedPrompt) {
    thePlan = fixAndParseJSON(enhancedPrompt);
  }

  const handleGenerate = async () => {
    setEnhancedPrompt("");
    setIsStreaming(true);

    let apps: string[] = [];

    if (userContext.connectedApps.length > 0) {
      apps = userContext.connectedApps.map((app) => app.name);
    }

    triggerRequest({
      prompt,
      apps: apps.length > 0 ? apps : undefined,
      withIntegrations: true,
    });

    await emitUserInteractionEvent({
      eventAction: "click",
      interactionGoal: "Generated an enriched prompt",
      rawText: prompt,
    });
  };

  return (
    <GradientBorder>
      <div className={styles.promptBoxContent}>
        <div>
          <div id="ai-prompt-box-header" className={styles.header}>
            <Text className={styles.headerText}>
              Get a system recommendation
            </Text>
            <Tag variant="simple">
              <Grid alignItems="center" columns="auto max-content" gap={2}>
                <Icon color="TextWeakest" name="miscEvergreenAI" size={14} />
                <Text type="MinimalPrint2" color="TextWeakest">
                  AI
                </Text>
              </Grid>
            </Tag>
          </div>
        </div>
        <div className={styles.promptInputRow}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={prompt}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you're building..."
            rows={1} // Start with a single row
          />
          <Button
            className={styles.button}
            isPending={isStreaming}
            variant="secondary"
            onPress={handleGenerate}
          >
            Generate system
          </Button>
        </div>
        <AiPromptBuilder
          emitUserInteractionEvent={emitUserInteractionEvent}
          isStreaming={isStreaming}
          handleSubmit={handleSubmit}
          originalPlan={thePlan}
          showAgentsNodes={showAgentsNodes}
        />
      </div>
    </GradientBorder>
  );
}
